// Ids in the booking URLs are Postgres UUIDs. Recipient-side mail-security
// scanners follow the links in confirmation emails and hand back a re-encoded
// final path segment — base64 of the UUID cut to 10 characters, so
// f3b8a5d7-be7f-448b-aae4-73fa8c767e4c arrives as ZjNiOGE1ZD — which can never
// match a record. Only ~7 of the 36 characters survive, so the original is not
// recoverable; checking the shape lets callers skip a request certain to 404.
//
// Any RFC 4122 version and variant is accepted rather than pinning v4: this
// rejects mangled ids, it does not police how a valid id was generated.
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUUID(value: string | undefined): boolean {
  return value !== undefined && UUID_PATTERN.test(value);
}

/**
 * A random (version 4) UUID.
 *
 * `crypto.randomUUID` is missing on Safari before 15.4, which the build still
 * targets, and on any page not served from a secure context. `getRandomValues`
 * has neither gap and draws from the same source, so the fallback is just as
 * unguessable.
 */
export function createUUID(): string {
  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return createUUIDFromRandomValues();
}

function createUUIDFromRandomValues(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));

  // RFC 4122 section 4.4: the high nibble of byte 6 carries the version (4)
  // and the top two bits of byte 8 the variant (binary 10).
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
}
